import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import * as symbolUtils from "@arcgis/core/symbols/support/symbolUtils.js";
import { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppConfig, LayersConfig } from "../../../AppConfig";
import { AppContext, LighthouseContext } from "../../../Context";
import DataTableIcon from "../../../assets/datatable.svg?react";
import TableEditIcon from "../../../assets/tableEdit.svg?react";
import ReturnIcon from "../../../assets/arrow-back.svg?react";
import CloseIcon from "../../../assets/close.svg?react";
import OptionsIcon from "../../../assets/options.svg?react";
import { RightPanelTabs } from "../../../constants/Panels";
import { setHasUnsavedChanges, setLoading, setRightPanelActiveTab } from "../../../store/appSlice";
import {
    setIsDrawing,
    setIsSavingLighthouse,
    setIsSectorEditorOpen,
    setIsSectorEditTableOpen,
    setIsSectorTableOpen,
    setIsShowingIALAWarning,
    setSectorOpacity,
    setSectorOrder,
    setShowSectorColors,
} from "../../../store/lighthouseSlice";
import { StoreState } from "../../../store/rootReducer";
import { getMapView } from "../../../utils/arcgisUtils";
import { Sector, SectorColor } from "../../../utils/lighthouseConstants";
import {
    hasSameColorConsecutiveSectors,
    hideLighthouses,
    isLighthouse,
    removeLighthouseEditingOverlay,
    satisfiesIALA,
    saveLighthouse,
    shiftSectors,
    unhideLighthouses,
} from "../../../utils/lighthouseUtils";
import { applySectorOrder } from "../../../utils/sectorRules";
import FeatureViewerTitle from "../../FeatureViewer/FeatureViewerTitle";
import ErrorMessage from "../../genericComponents/ErrorMessage/ErrorMessage";
import NfsButton from "../../genericComponents/NfsButton/NfsButton";
import RangeSlider from "../../genericComponents/RangeSlider/RangeSlider";
import Toggle from "../../genericComponents/Toggle/Toggle";
import "./SectorEditorContent.css";
import SectorMenu from "./SectorMenu";
import SectorRose from "./SectorRose";
import { drawSectors } from "./lighthouseDrawing";
import { NFSStatus, setNewStatusToFeature } from "../../../utils/nfsUtils";
import { Popover } from "react-tiny-popover";
import RadioButtonGroup from "../../genericComponents/RadioButton/RadioButtonGroup";
import type FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import type Graphic from "@arcgis/core/Graphic";
import type Collection from "@arcgis/core/core/Collection";

interface SectorEditorContentProps {
    closeSectorEditing: (warn: boolean) => void;
}

const SectorEditorContent = (props: SectorEditorContentProps) => {
    const appContext = useContext(AppContext);
    const lighthouseContext = useContext(LighthouseContext);

    const selectedFeature = appContext?.selectedFeature.value;
    const lighthouseDataState = lighthouseContext?.lighthouseData.value;
    const selectedLineRef = lighthouseContext?.selectedLineRef.value;

    const dispatch = useDispatch();
    const isShowingIALAWarning = useSelector((state: StoreState) => state.lighthouse.isShowingIALAWarning);
    const isSectorTableOpen = useSelector((state: StoreState) => state.lighthouse.isSectorTableOpen);
    const isSectorEditTableOpen = useSelector((state: StoreState) => state.lighthouse.isSectorEditTableOpen);
    const lighthouseTableSelectedSector = useSelector(
        (state: StoreState) => state.lighthouse.lighthouseTableSelectedSector
    );
    const mapScale = useSelector((state: StoreState) => state.app.mapScale);
    const trueNorth = useSelector((state: StoreState) => state.lighthouse.currentLighthouseTrueNorth);
    const isEditing = useSelector((state: StoreState) => state.lighthouse.isEditing);
    const showSectorColors = useSelector((state: StoreState) => state.lighthouse.showSectorColors);
    const sectorOpacity = useSelector((state: StoreState) => state.lighthouse.sectorOpacity);
    const hasShownIALAWarning = useSelector((state: StoreState) => state.lighthouse.isShowingIALAWarning);
    const showRemovedFeatures = useSelector((state: any) => state.app.showRemovedFeatures);
    const sectorOrder = useSelector((state: StoreState) => state.lighthouse.sectorOrder);

    const [symbolHtmlElement, setSymbolHtmlElement] = useState<HTMLElement | null>(null);
    const [hideOtherSectorLines, setHideOtherSectorLines] = useState(false);
    const [hideOtherSectorAreas, setHideOtherSectorAreas] = useState(false);
    const [sectors, setSectors] = useState<Sector[]>([]);
    // Layers hidden from the options menu, so they can be shown again when editing ends
    const hiddenLayerIds = useRef(new Set<string>());

    const [optionsPopoverOpen, setOptionsPopoverOpen] = useState(false);
    const [unitSelectedIndex, setUnitSelectedIndex] = useState(0);

    const layerConfig = useRef<LayersConfig>(undefined);

    useEffect(() => {
        const mapview = getMapView();
        if (!mapview) return;

        hideLighthouses([selectedFeature], showRemovedFeatures);

        return () => {
            unhideLighthouses(showRemovedFeatures);
        };
    }, []);

    useEffect(() => {
        const layerIds = hiddenLayerIds.current;
        return () => {
            const map = getMapView()?.map;
            for (const layerId of layerIds) {
                const layer = map?.findLayerById(layerId);
                if (layer) layer.visible = true;
            }
            layerIds.clear();
        };
    }, []);

    // Snapshot the numbering order when editing starts, so the sector lists and numbers do not
    // reorder while the user edits. Saving here closes the editor; a table save takes a new one.
    useEffect(() => {
        if (sectorOrder === null && lighthouseDataState) {
            dispatch(setSectorOrder(shiftSectors(lighthouseDataState).map((sector) => sector.globalid)));
        }
    }, [lighthouseDataState, sectorOrder, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(setSectorOrder(null));
        };
    }, [dispatch]);

    useEffect(() => {
        if (!lighthouseDataState || !selectedFeature) return;

        selectedFeature.attributes.lighthouseData = lighthouseDataState;
        appContext.selectedFeature.set(selectedFeature);

        const mapview = getMapView();
        if (!mapview?.map) return;

        const lighthouseEditingLayer = mapview.map.findLayerById("FyrlyktEditing") as GraphicsLayer;

        const showAllSectors = showSectorColors
            ? new Array(lighthouseDataState.sectors.length).fill(0).map((_, i) => i)
            : [];

        if (!isLighthouse(selectedFeature)) {
            if (lighthouseDataState.sectors.length === 1) {
                lighthouseDataState.sectors[0].color = selectedFeature.attributes.lys_farge.toString();
            }

            for (const sector of lighthouseDataState.sectors) {
                if (sector.color !== SectorColor.DARK) {
                    sector.color = selectedFeature.attributes.lys_farge.toString();
                }
            }
            if (
                lighthouseDataState.sectors.length > 1 &&
                !lighthouseDataState.sectors.some((sector) => sector.color === SectorColor.DARK)
            ) {
                lighthouseDataState.sectors[0].color = SectorColor.DARK;
            }
        }

        drawSectors({
            graphicsLayer: lighthouseEditingLayer,
            data: lighthouseDataState,
            setIsEditing: (value) => dispatch(setIsDrawing(value)),
            sectorsToFill: lighthouseTableSelectedSector === null ? showAllSectors : [lighthouseTableSelectedSector],
            opacity: sectorOpacity,
            drawNumbers: false,
            trueNorthCorrection: trueNorth,
            centerFeature: selectedFeature,
        });

        const sectorsShifted = applySectorOrder(lighthouseContext.lighthouseData.value, sectorOrder);

        setSectors(sectorsShifted);
    }, [
        lighthouseContext?.lighthouseData.value,
        selectedFeature,
        lighthouseTableSelectedSector,
        mapScale,
        sectorOrder,
    ]);

    useEffect(() => {
        const mapview = getMapView();
        if (!mapview?.map || !selectedFeature) return;

        const lighthouseEditingLayer = mapview.map.findLayerById("FyrlyktEditing") as GraphicsLayer;

        const showAllSectors = showSectorColors
            ? new Array(selectedFeature?.attributes.lighthouseData.sectors.length).fill(0).map((_, i) => i)
            : [];

        drawSectors({
            graphicsLayer: lighthouseEditingLayer,
            data: selectedFeature?.attributes.lighthouseData,
            setIsEditing: setIsDrawing,
            sectorsToFill: lighthouseTableSelectedSector === null ? showAllSectors : [lighthouseTableSelectedSector],
            opacity: sectorOpacity,
            drawNumbers: false,
            trueNorthCorrection: trueNorth,
            centerFeature: selectedFeature,
        });
    }, [showSectorColors, sectorOpacity, lighthouseTableSelectedSector, mapScale]);

    useEffect(() => {
        if (isEditing) return;
        if (!selectedFeature?.attributes.lighthouseData) return;

        const mapview = getMapView();
        if (!mapview?.map) return;

        const lighthouseEditingLayer = mapview.map.layers.find(
            (layer) => layer.id === "FyrlyktEditing"
        ) as GraphicsLayer;

        lighthouseEditingLayer.load().then(() => {
            const graphics = lighthouseEditingLayer.graphics;

            updateSectors(graphics);
        });
    }, [isEditing]);

    useEffect(() => {
        if (!selectedFeature) {
            dispatch(setIsSectorTableOpen(false));
            dispatch(setRightPanelActiveTab(RightPanelTabs.UNDEFINED));
            dispatch(setIsSectorEditorOpen(false));
            dispatch(setIsShowingIALAWarning(false));
            if (lighthouseDataState) {
                saveLighthouse(
                    lighthouseDataState,
                    appContext?.selectedFeature.set,
                    selectedFeature!,
                    trueNorth,
                    (isSaving) => dispatch(setIsSavingLighthouse(isSaving)),
                    (loadingStatus) => dispatch(setLoading(loadingStatus))
                );
            }
            return;
        }

        layerConfig.current = AppConfig.Layers.find((layerConfig) => layerConfig.Name === selectedFeature.layer?.id);

        symbolUtils.getDisplayedSymbol(selectedFeature).then((symbol) => {
            if (symbol) {
                symbolUtils.renderPreviewHTML(symbol, { size: 12 }).then((symbolElement) => {
                    if (!symbolElement) return;
                    setSymbolHtmlElement(symbolElement);
                });
            } else {
                setSymbolHtmlElement(null);
            }
        });
    }, [selectedFeature]);

    const updateSectors = (graphics: Collection<Graphic>) => {
        const mapview = getMapView();
        if (!mapview?.map) return;

        const lighthouseEditingLayer = mapview.map.findLayerById("FyrlyktEditing") as GraphicsLayer;
        lighthouseEditingLayer.visible = true;

        for (const [index, sector] of lighthouseDataState?.sectors.entries() ?? []) {
            const sectorLine = graphics.find((graphic) => {
                return graphic.attributes?.index === sector.index && graphic.attributes?.objectType === "line";
            });
            if (!sectorLine) return;

            const newSectorLine = sectorLine.clone();
            newSectorLine.attributes.index = sector.index;
            lighthouseEditingLayer.remove(sectorLine);
            lighthouseEditingLayer.add(newSectorLine);

            sector.index = index;
        }
    };

    const toggleIsSectorTableOpen = () => {
        dispatch(setIsSectorTableOpen(!isSectorTableOpen));
    };

    // The edited lighthouse is drawn in FyrlyktEditing, so hiding these layers only hides the others
    const setLayersHidden = (layerIds: string[], hidden: boolean) => {
        const mapview = getMapView();
        if (!mapview?.map) return;

        for (const layerId of layerIds) {
            const layer = mapview.map.findLayerById(layerId);
            if (!layer) continue;

            layer.visible = !hidden;
            if (hidden) hiddenLayerIds.current.add(layerId);
            else hiddenLayerIds.current.delete(layerId);
        }
    };

    const setOtherSectorLinesHidden = (value: boolean) => {
        setLayersHidden(["Sektorlinje", "Overettlinje lys", "Overettlinje merke"], value);
        setHideOtherSectorLines(value);
    };

    const setOtherSectorAreasHidden = (value: boolean) => {
        setLayersHidden(["Sektorflate"], value);
        setHideOtherSectorAreas(value);
    };

    // A dark sector keeps the number of the lit sector before it, for its line labels
    let displayIndex = -1;
    if (!selectedFeature) return null;

    const saveSectors = () => {
        if (hasSameColorConsecutiveSectors(lighthouseContext?.lighthouseData.value.sectors ?? [])) return;

        if (!satisfiesIALA(lighthouseContext!.lighthouseData.value) && !hasShownIALAWarning) {
            dispatch(setIsShowingIALAWarning(true));
            return;
        }
        dispatch(setHasUnsavedChanges(false));
        lighthouseContext?.lighthouseSectorPanelsOpen.set(new Set());
        dispatch(setIsShowingIALAWarning(false));
        dispatch(setIsSectorEditorOpen(false));

        const mapView = getMapView();
        const lighthouseEditingLayer = mapView?.map?.findLayerById("FyrlyktEditing") as GraphicsLayer;
        lighthouseEditingLayer?.removeAll();
        removeLighthouseEditingOverlay();
        setNewStatusToFeature(selectedFeature, NFSStatus.PROJECTED_EXISTING);
        saveLighthouse(
            lighthouseContext!.lighthouseData.value,
            appContext?.selectedFeature.set,
            appContext.selectedFeature.value!,
            trueNorth,
            (isSaving: boolean) => dispatch(setIsSavingLighthouse(isSaving)),
            (loadingStatus: boolean) => dispatch(setLoading(loadingStatus))
        ).then(() => {
            const clonedFeature = selectedFeature.clone();
            clonedFeature.layer = selectedFeature.layer;
            appContext.selectedFeature.set(clonedFeature);
            dispatch(setRightPanelActiveTab(RightPanelTabs.EDITOR));
        });

        const lightLayer = mapView?.map?.findLayerById("Lys") as FeatureLayer;
        const sectorLayer = mapView?.map?.findLayerById("Sektorflate") as FeatureLayer;
        const lineLayer = mapView?.map?.findLayerById("Sektorlinje") as FeatureLayer;
        lightLayer.visible = true;
        sectorLayer.visible = true;
        lineLayer.visible = true;

        selectedLineRef!.current = null;
        dispatch(setIsDrawing(false));
    };

    const hasConsecutiveSectors = hasSameColorConsecutiveSectors(lighthouseContext?.lighthouseData.value.sectors ?? []);

    return (
        <>
            <div className="feature-viewer-content">
                <div className="sector-editor-header">
                    <button
                        onClick={() => props.closeSectorEditing(true)}
                        className="empty-button flex-row center"
                        disabled={isSectorEditTableOpen}
                    >
                        <ReturnIcon />
                        Tilbake
                    </button>
                    <FeatureViewerTitle
                        symbolHtmlElement={symbolHtmlElement}
                        feature={appContext?.selectedFeature.value!}
                        layerConfig={layerConfig}
                        canMove={false}
                    />
                    <NfsButton onClick={toggleIsSectorTableOpen} round inverted small active={isSectorTableOpen}>
                        <DataTableIcon />
                    </NfsButton>
                    {isLighthouse(selectedFeature) && (
                        <NfsButton
                            onClick={() => dispatch(setIsSectorEditTableOpen(true))}
                            title="Rediger sektorene i tabell"
                            round
                            inverted
                            small
                        >
                            <TableEditIcon />
                        </NfsButton>
                    )}
                    <Popover
                        align="start"
                        isOpen={optionsPopoverOpen}
                        onClickOutside={() => setOptionsPopoverOpen(false)}
                        content={
                            <div className="sector options-popover-content">
                                <span className="visible-when-alone">Ingen gyldige handlinger</span>
                                <div
                                    className="flex-row small-gap align-center space-between"
                                    title="Skjuler også overettlinjer"
                                >
                                    <span>Skjul andre sektorlinjer</span>
                                    <Toggle checked={hideOtherSectorLines} onChange={setOtherSectorLinesHidden} />
                                </div>
                                <div className="flex-row small-gap align-center space-between">
                                    <span>Skjul andre sektorer</span>
                                    <Toggle checked={hideOtherSectorAreas} onChange={setOtherSectorAreasHidden} />
                                </div>
                                <div className="flex-row small-gap align-center space-between">
                                    <span>Utvid sektorfarger i kart</span>
                                    <Toggle
                                        checked={showSectorColors}
                                        onChange={(value) => dispatch(setShowSectorColors(value))}
                                    />
                                </div>
                                <label htmlFor="sector-opacity-slider">Gjennomsiktighet sektorer</label>
                                <RangeSlider
                                    id="sector-opacity-slider"
                                    value={sectorOpacity}
                                    onChange={(value) => dispatch(setSectorOpacity(value))}
                                />
                                <RadioButtonGroup
                                    options={["Vis i Nm", "Vis i m"]}
                                    selectedIndex={unitSelectedIndex}
                                    onChange={(selectedIndex) => {
                                        setUnitSelectedIndex(selectedIndex);
                                    }}
                                />
                            </div>
                        }
                    >
                        <button
                            type="button"
                            className="empty-button"
                            onClick={() => {
                                setOptionsPopoverOpen(!optionsPopoverOpen);
                            }}
                        >
                            <OptionsIcon />
                        </button>
                    </Popover>
                    <button
                        type="button"
                        className="empty-button"
                        onClick={() => props.closeSectorEditing(true)}
                        disabled={isSectorEditTableOpen}
                    >
                        <CloseIcon />
                    </button>
                </div>
                <SectorRose />
                {isShowingIALAWarning ? <ErrorMessage message="Sektorinndelingen er ikke i henhold til IALA." /> : null}
                {/* The table holds a draft of these same sectors. Editing them here at the same
                    time would leave that draft stale, and saving it would undo the edits. */}
                {isSectorEditTableOpen ? (
                    <p className="sector-editor-table-open-notice">
                        Sektorene redigeres i tabellen. Lukk tabellen for å redigere dem her.
                    </p>
                ) : null}
                {isSectorEditTableOpen
                    ? null
                    : sectors.map((sector: Sector) => {
                          if (sector.color !== SectorColor.DARK) displayIndex++;
                          return (
                              <SectorMenu
                                  key={`${lighthouseContext?.lighthouseData.value.id}_${sector.index}`}
                                  sectorIndex={sector.index}
                                  unitSelectedIndex={unitSelectedIndex}
                                  displayIndex={Math.max(displayIndex, 0)}
                              />
                          );
                      })}
            </div>
            <div className="sector-editor-save-button">
                <NfsButton
                    onClick={saveSectors}
                    disabled={hasConsecutiveSectors || isSectorEditTableOpen}
                    title={
                        hasConsecutiveSectors
                            ? "Kan ikke lagre fyrlykt som har tilstøtende sektorer med samme farge"
                            : undefined
                    }
                >
                    Lagre
                </NfsButton>
            </div>
        </>
    );
};

export default SectorEditorContent;
